import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from './AuthContext';

type PermissionLevel = 'none' | 'view' | 'full';

interface PermissionContextType {
  permissions: Record<string, PermissionLevel>;
  loading: boolean;
  canView: (module: string) => boolean;
  canEdit: (module: string) => boolean;
  refreshPermissions: () => void;
}

const PermissionContext = createContext<PermissionContextType | null>(null);

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState<Record<string, PermissionLevel>>({});
  const [loading, setLoading] = useState(true);

  const fetchPermissions = async () => {
    if (!user) { setLoading(false); return; }
    if (user.role === 'admin') {
      const allFull: Record<string, PermissionLevel> = {};
      ['dashboard', 'products', 'suppliers', 'clients', 'sales_quotations', 'service_quotations', 'tenders', 'purchases', 'sales_cases', 'service_cases', 'accounting', 'warehouse', 'service_module', 'service_acts', 'users']
        .forEach(m => allFull[m] = 'full');
      setPermissions(allFull);
      setLoading(false);
      return;
    }
    try {
      const res = await api.get('/my-permissions');
      setPermissions(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => { fetchPermissions(); }, [user]);

  const canView = (module: string) => {
    const level = permissions[module];
    return level === 'view' || level === 'full';
  };

  const canEdit = (module: string) => permissions[module] === 'full';

  return (
    <PermissionContext.Provider value={{ permissions, loading, canView, canEdit, refreshPermissions: fetchPermissions }}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => {
  const ctx = useContext(PermissionContext);
  if (!ctx) throw new Error('usePermissions must be used within PermissionProvider');
  return ctx;
};